import React, { forwardRef } from 'react';
import { TreasuryBillLifecycle, TreasuryBillTransaction } from '@typeDefs/domain/assets';
import { formatCalendarDate, formatLondonDateTime } from '@utils/formatters';
import styles from './TransactionTimeline.module.css';

interface TimelineStep {
  stepNumber: number;
  title: string;
  date: string;
  isCompleted: boolean;
}

export interface ITransactionTimelineProps extends React.HTMLAttributes<HTMLDivElement> {
  transactions: TreasuryBillTransaction[];
  maturityDate: Date;
  status?: TreasuryBillLifecycle;
  isMatured?: boolean;
}

const TRANSACTION_STEP_LABELS: Record<string, string> = {
  request: 'Purchase request received',
  approval: 'Purchase approved',
  rejection: 'Purchase rejected',
  issue: 'Treasury bill issued',
  maturity: 'Treasury bill matured',
  'sale-request': 'Sale request received',
  sale: 'Treasury bill sold',
  'stake-sale-request': 'Partial sale request received',
  'stake-sale': 'Treasury bill partially sold',
  other: 'Transaction processed',
};

const TransactionTimeline = forwardRef<HTMLDivElement, ITransactionTimelineProps>(
  ({ transactions, maturityDate, status, isMatured, className, ...rest }, ref) => {
    const lifecycle = status ?? (isMatured ? 'matured' : 'active');
    const sortedTransactions = [...transactions].sort(
      (left, right) => left.transactionDateTime.getTime() - right.transactionDateTime.getTime(),
    );
    const transactionSteps: TimelineStep[] = sortedTransactions.map((transaction) => ({
      stepNumber: 0,
      title: (
        transaction.transactionDescriptionDisplay ||
        TRANSACTION_STEP_LABELS[transaction.transactionType] ||
        transaction.transactionType.charAt(0).toUpperCase() + transaction.transactionType.slice(1)
      ).replace(/�/g, '£'),
      date: formatLondonDateTime(transaction.transactionDateTime),
      isCompleted: true,
    }));

    const hasPersistedMaturity = transactions.some(
      (transaction) => transaction.transactionType === 'maturity',
    );
    const syntheticMaturitySteps: TimelineStep[] =
      lifecycle !== 'sold' && !hasPersistedMaturity
        ? [
            {
              stepNumber: 0,
              title: 'Treasury bill maturity',
              date: formatCalendarDate(maturityDate),
              isCompleted: lifecycle === 'matured',
            },
          ]
        : [];

    const allSteps = [...transactionSteps, ...syntheticMaturitySteps].map((step, index) => ({
      ...step,
      stepNumber: index + 1,
    }));

    return (
      <div ref={ref} className={[styles.timeline, className].filter(Boolean).join(' ')} {...rest}>
        {allSteps.map((step, index) => (
          <div key={index} className={styles.timelineItem}>
            <div className={styles.stepIndicator}>
              <div
                className={`${styles.stepCircle} ${step.isCompleted ? styles.completed : styles.ongoing}`}
              >
                {step.stepNumber}
              </div>
              {index < allSteps.length - 1 && (
                <div
                  className={`${styles.stepConnector} ${
                    step.isCompleted && allSteps[index + 1].isCompleted
                      ? styles.connectorCompleted
                      : styles.connectorOngoing
                  }`}
                />
              )}
            </div>
            <div className={styles.stepContent}>
              <div
                className={`${styles.stepTitle} ${step.isCompleted ? styles.titleCompleted : styles.titleOngoing}`}
              >
                {step.title}
              </div>
              <div
                className={`${styles.stepDate} ${step.isCompleted ? styles.dateCompleted : styles.dateOngoing}`}
              >
                {step.date}
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  },
);

TransactionTimeline.displayName = 'TransactionTimeline';

export default TransactionTimeline;
